import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FiBriefcase, FiCalendar, FiMapPin } from 'react-icons/fi';

const ExperienceSection = styled.section`
  padding: 100px 150px;
  
  @media (max-width: 768px) {
    padding: 80px 25px;
  }
`;

const ExperienceContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const ExperienceTitle = styled(motion.h2)`
  display: flex;
  align-items: center;
  font-size: 32px;
  font-weight: 600;
  margin-bottom: 40px;
  color: var(--text-primary);
  
  &::after {
    content: "";
    display: block;
    width: 300px;
    height: 1px;
    margin-left: 20px;
    background-color: var(--bg-highlight);
  }
  
  @media (max-width: 768px) {
    font-size: 24px;
    
    &::after {
      width: 100px;
    }
  }
`;

const TabContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  margin-top: 30px;
  position: relative;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const TabList = styled.div`
  position: relative;
  z-index: 3;
  width: 200px;
  
  @media (max-width: 768px) {
    display: flex;
    overflow-x: auto;
    width: 100%;
    margin-bottom: 30px;
  }
`;

const VerticalLine = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 2px;
  height: 100%;
  background-color: var(--bg-highlight);
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const HighlightBar = styled(motion.div)`
  position: absolute;
  width: 2px;
  height: 42px;
  background-color: var(--accent-primary);
  left: 0;
  top: ${props => props.$activeTab * 42}px;
  transition: top 0.3s ease-in-out;
  
  @media (max-width: 768px) {
    width: 100%;
    height: 2px;
    top: auto;
    bottom: 0;
    left: ${props => props.$activeTab * 180}px;
    width: 180px;
    transition: left 0.3s ease-in-out;
  }
`;

const TabButton = styled.button`
  text-align: left;
  padding: 10px 20px;
  width: 100%;
  height: 42px;
  background-color: transparent;
  color: ${props => props.$isActive ? 'var(--accent-primary)' : 'var(--text-secondary)'};
  font-family: var(--font-mono);
  font-size: 14px;
  transition: all 0.3s ease-in-out;
  border: none;
  border-left: 2px solid ${props => props.$isActive ? 'transparent' : 'var(--bg-highlight)'};
  cursor: pointer;
  position: relative;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  
  &:hover {
    color: var(--accent-primary);
    background-color: rgba(100, 255, 218, 0.07);
  }
  
  @media (max-width: 768px) {
    width: 180px;
    min-width: 180px;
    border-left: none;
    border-bottom: 2px solid ${props => props.$isActive ? 'transparent' : 'var(--bg-highlight)'};
    font-size: 13px;
  }
`;

const TabContent = styled(motion.div)`
  padding-left: 30px;
  flex: 1;
  
  @media (max-width: 768px) {
    padding-left: 0;
  }
`;

const JobTitle = styled.h3`
  color: var(--text-primary);
  font-size: 22px;
  font-weight: 600;
  margin-bottom: 5px;
  display: flex;
  align-items: center;
  
  span {
    color: var(--accent-primary);
  }
  
  @media (max-width: 768px) {
    font-size: 18px;
    flex-direction: column;
    align-items: flex-start;
    line-height: 1.5;
    
    span {
      margin-top: 2px;
      font-size: 16px;
    }
  }
`;

const CompanyInfo = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 20px;
  color: var(--text-secondary);
  font-family: var(--font-mono);
  font-size: 14px;
  
  svg {
    margin-right: 8px;
  }
  
  .company-date {
    display: flex;
    align-items: center;
    margin-right: 20px;
    margin-bottom: 5px;
  }
  
  .company-location {
    display: flex;
    align-items: center;
    margin-bottom: 5px;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    margin-top: 10px;
    
    .company-date, .company-location {
      margin-bottom: 10px;
    }
    
    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

const Description = styled.div`
  color: var(--text-secondary);
  
  ul {
    padding-left: 20px;
  }
  
  li {
    position: relative;
    padding-left: 25px;
    margin-bottom: 15px;
    line-height: 1.6;
    
    &::before {
      content: '▹';
      position: absolute;
      left: 0;
      color: var(--accent-primary);
      font-size: 16px;
    }
  }
`;

const SideHighlightItem = styled(motion.li)`
  position: relative;
  padding-left: 25px;
  margin-bottom: 15px;
  line-height: 1.6;
  
  &::before {
    content: '▹';
    position: absolute;
    left: 0;
    color: var(--accent-primary);
    font-size: 16px;
  }
`;

const tabContentVariants = {
  hidden: { opacity: 0, x: 20 },
  visible: { 
    opacity: 1, 
    x: 0,
    transition: {
      duration: 0.5,
      when: "beforeChildren",
      staggerChildren: 0.1
    }
  },
  exit: {
    opacity: 0,
    x: -20,
    transition: { duration: 0.3 }
  }
};

const listItemVariants = {
  hidden: { opacity: 0, x: 10 },
  visible: { 
    opacity: 1, 
    x: 0,
    transition: { duration: 0.3 }
  }
};

const Experience = () => {
  const [activeTab, setActiveTab] = useState(0);
  
  const [titleRef, titleInView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  
  const experiences = [
    {
      company: "KPMG India",
      title: "Cyber Security Consultant",
      date: "April 2024 - Present",
      location: "Bengaluru, India",
      description: [
        "Conducted comprehensive penetration testing for one of the world's top 3 global investment banks across Mobile Applications, Web Applications, APIs, Cloud, Thick Client and Network & Infrastructure. Identified multiple vulnerabilities and strengthened security measures.",
        "Developed and implemented Python-based automation tools to enhance Software Development Life Cycle (SDLC) efficiency. Automated retrieval of open findings from sources such as Checkmarx, Semgrep, CatchIT, and Prisma CWPP via the Internal Portal.",
        "Created multiple in-house security tools, including Path Finder and fuzzers, using Python to address gaps in corporate environments where open-source tools were unavailable or restricted, enhancing security testing and vulnerability detection capabilities.",
        "Performed manual and automated secure code reviews of Go, Java, Python, and JavaScript-based applications using Checkmarx, identifying vulnerabilities such as SQL injection, XSS, and other OWASP Top 10-related vulnerabilities, and provided remediation guidance.",
        "Collaborated as a Triager on HackerOne, validating vulnerabilities, researching mitigation strategies, and automating detection scripts for firm-wide vulnerability identification and remediation based on HackerOne reports."
      ]
    },
    {
      company: "Tata Consultancy Services",
      title: "Cyber Security Analyst",
      date: "September 2021 - April 2024",
      location: "Bengaluru, India",
      description: [
        "Led penetration testing initiatives for a leading Japanese fintech company in the Buy Now, Pay Later (BNPL) sector.",
        "Created custom exploits and proof-of-concept scripts to demonstrate the impact of security vulnerabilities.",
        "Performed threat modeling to proactively identify design-level security risks in application architectures, collaborating with development teams to prioritize and mitigate potential attack vectors.",
        "Prepared comprehensive security reports including actionable remediation strategies, risk assessments, and code snippets to facilitate vulnerability mitigation.",
        "Acted as a key internal security resource, providing expert guidance and support for security engineering-related inquiries."
      ]
    }
  ];
  
  return (
    <ExperienceSection id="experience">
      <ExperienceContainer>
        <ExperienceTitle
          ref={titleRef}
          initial={{ y: 20, opacity: 0 }}
          animate={titleInView ? { y: 0, opacity: 1 } : { y: 20, opacity: 0 }}
          transition={{ duration: 0.5 }}
        >
          Experience
        </ExperienceTitle>
        
        <TabContainer>
          <TabList>
            <VerticalLine />
            <HighlightBar 
              $activeTab={activeTab}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            />
            {experiences.map((experience, index) => (
              <TabButton
                key={index}
                $isActive={activeTab === index}
                onClick={() => setActiveTab(index)}
              >
                {experience.company}
              </TabButton>
            ))}
          </TabList>
          
          <TabContent
            key={activeTab}
            variants={tabContentVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <JobTitle>
              {experiences[activeTab].title}
              <span> @ {experiences[activeTab].company}</span>
            </JobTitle>
            
            <CompanyInfo>
              <div className="company-date">
                <FiCalendar />
                {experiences[activeTab].date}
              </div>
              <div className="company-location">
                <FiMapPin />
                {experiences[activeTab].location}
              </div>
            </CompanyInfo>
            
            <Description>
              <ul>
                {experiences[activeTab].description.map((point, i) => (
                  <SideHighlightItem
                    key={i}
                    variants={listItemVariants}
                  >
                    {point}
                  </SideHighlightItem>
                ))}
              </ul>
            </Description>
          </TabContent>
        </TabContainer>
      </ExperienceContainer>
    </ExperienceSection>
  );
};

export default Experience; 
